import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
    Box,
    Button,
    Chip,
    Container,
    Grid,
    Paper,
    Stack,
    Typography,
} from '@mui/material';
import {
    CloudUpload,
    Code,
    Lock,
    Loop,
} from '@mui/icons-material';
import AgentCodeEditor from './Games/AgentCodeEditor';
import ReplayPlayer from './ReplayPlayer';
import ManualSaveNotice from './ManualSaveNotice';
import GameHeader from './ui/GameHeader';
import usePageTitle from './ui/usePageTitle';
import LeaderboardPanel from './ui/LeaderboardPanel';
import GmLeaseNotice from './GmLeaseNotice';
import UploadAccountLinks from './UploadAccountLinks';
import {
    ARCADE_THEME,
    GROW_PLAYFIELD_SHELL_LAYOUT,
    formatArcadeTimestamp,
    leaderboardEntryScore,
} from './arcadeTheme';
import {
    ArcadeReplayDialog,
    LeaderboardWatchButton,
    useArcadeReplay,
} from './arcadeReplay';
import { shouldShowReplay } from './useArcadeAgentSession';
import { COLOR } from './ui/tokens';

const THEME = ARCADE_THEME;

function hexToRgb(hex) {
    const cleaned = String(hex || '').trim().replace('#', '');
    if (![3, 6].includes(cleaned.length)) return null;
    const full = cleaned.length === 3
        ? cleaned.split('').map((c) => `${c}${c}`).join('')
        : cleaned;
    const intVal = Number.parseInt(full, 16);
    if (!Number.isFinite(intVal)) return null;
    return {
        r: (intVal >> 16) & 255,
        g: (intVal >> 8) & 255,
        b: intVal & 255,
    };
}

function accentSurfaceColor(accent) {
    const rgb = hexToRgb(accent);
    if (!rgb) return 'rgba(255,255,255,0.06)';
    return `rgba(${rgb.r}, ${rgb.g}, ${rgb.b}, 0.14)`;
}

function accentContrastText(accent) {
    const rgb = hexToRgb(accent);
    if (!rgb) return COLOR.bg;
    const luminance = ((0.299 * rgb.r) + (0.587 * rgb.g) + (0.114 * rgb.b)) / 255;
    return luminance > 0.6 ? COLOR.bg : COLOR.text;
}

/**
 * Column order: on phones the game comes first, then the leaderboard, then
 * the agent panel; from lg up the leaderboard is the left rail again.
 */
export const MOBILE_ORDER = {
    play: { xs: 1, lg: 2 },
    leaderboard: { xs: 2, lg: 1 },
    agent: { xs: 3, lg: 3 },
};

/** sx for one column: the phone order as the base rule, the desktop one from lg. */
const orderSx = (key, extra = {}) => (theme) => ({
    ...extra,
    order: MOBILE_ORDER[key].xs,
    [theme.breakpoints.up('lg')]: { order: MOBILE_ORDER[key].lg },
});

export { usePageTitle };

/**
 * Page frame shared by every game: GameHeader, then the leaderboard, play
 * and agent columns (game first on phones, see MOBILE_ORDER). Games with
 * their own panels (Snake, Sliding Tiles) use it directly; ArcadeGameShell
 * wraps it with the standard panels. Extra nodes (dialogs) go in children.
 */
export function ArcadeShellLayout({
    title,
    titleAccent,
    subtitle,
    accentColor = THEME.primary,
    headerContent = null,
    growPlayfield = false,
    wideShell = GROW_PLAYFIELD_SHELL_LAYOUT,
    leaderboard,
    play,
    agent,
    children,
}) {
    usePageTitle([title, titleAccent].filter(Boolean).join(' '));
    return (
        <Box sx={{ bgcolor: THEME.bg, minHeight: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ flexGrow: 1, pb: 4 }}>
                <Container
                    maxWidth={growPlayfield ? false : 'xl'}
                    disableGutters={growPlayfield}
                    data-testid={growPlayfield ? 'arcade-shell-playfield-wide' : 'arcade-shell-standard'}
                    sx={growPlayfield ? { px: wideShell.outerPx, width: '100%' } : { px: { xs: 1.5, sm: 3 } }}
                >
                    <GameHeader
                        title={title}
                        titleAccent={titleAccent}
                        accentColor={accentColor}
                        subtitle={subtitle}
                        mode={headerContent}
                        compact={growPlayfield}
                    />

                    {growPlayfield ? (
                        <Box
                            sx={{
                                display: 'grid',
                                gridTemplateColumns: wideShell.gridColumns,
                                gap: wideShell.gridGap,
                                alignItems: 'stretch',
                            }}
                        >
                            <Box data-testid="shell-leaderboard" sx={orderSx('leaderboard', { minWidth: 0 })}>
                                {leaderboard}
                            </Box>
                            <Box
                                data-testid="shell-play"
                                sx={orderSx('play', { minWidth: 0, display: 'flex', flexDirection: 'column' })}
                            >
                                {play}
                            </Box>
                            <Box data-testid="shell-agent" sx={orderSx('agent', { minWidth: 0 })}>
                                {agent}
                            </Box>
                        </Box>
                    ) : (
                        <Grid container spacing={{ xs: 2, lg: 3 }} alignItems="stretch">
                            <Grid item xs={12} lg={3} data-testid="shell-leaderboard" sx={orderSx('leaderboard', { minWidth: 0 })}>
                                {leaderboard}
                            </Grid>
                            <Grid item xs={12} lg={6} data-testid="shell-play" sx={orderSx('play', { minWidth: 0 })}>
                                {play}
                            </Grid>
                            <Grid item xs={12} lg={3} data-testid="shell-agent" sx={orderSx('agent', { minWidth: 0 })}>
                                {agent}
                            </Grid>
                        </Grid>
                    )}
                </Container>
            </Box>
            {children}
        </Box>
    );
}

/**
 * Shared Arcade page chrome: title, leaderboard, play slot, auth-gated upload/editor.
 * Play UI is passed as children; agent boilerplate via `boilerplate`.
 */
export default function ArcadeGameShell({
    title,
    titleAccent,
    subtitle,
    accentColor = THEME.primary,
    boilerplate,
    session,
    children,
    formatScore,
    emptyLeaderboardHint = 'No scores yet. Upload an agent to get on the board.',
    belowLeaderboard,
    sidebarExtra,
    belowAgent,
    shellLayoutOverrides = null,
    /** Use inset dark play surface (#121214) on the center column — avoids nested gray boxes. */
    playfieldDark = false,
    /** Give the play column more width/height so a large canvas (e.g. Space Invader) can fill it. */
    growPlayfield = false,
    /** Optional header row content, e.g. a mode picker (TJP 1P / 2P). */
    headerContent = null,
}) {
    const {
        token,
        leaderboard,
        leaderboardLoading,
        refreshLeaderboard,
        agentFile,
        agentInputTab,
        setAgentInputTab,
        agentRunLoading,
        uploadMessage,
        uploadNeedsAccount,
        manualSaveNotice,
        gmLease,
        lastRun,
        lastRunLoading,
        run,
        handleAgentFileChange,
        handleRunAgent,
        handleEditorSubmit,
    } = session;
    const showAgentReplay = shouldShowReplay(run, agentRunLoading, { only_replayable: true });
    const showLastRunReplay = shouldShowReplay(lastRun, false, { only_replayable: true });
    const [agentReplayDismissed, setAgentReplayDismissed] = useState(false);
    const runKey = run
        ? String(run.id || run._id || run.run_id || run.db_ref || '')
        : '';

    useEffect(() => {
        setAgentReplayDismissed(false);
    }, [runKey]);


    const accent = accentColor || THEME.primary;
    const lastRunCardBg = accentSurfaceColor(accent);
    const lastRunChipText = accentContrastText(accent);
    const header = boilerplate?.header || '';
    const template = boilerplate?.template || '';
    const footer = boilerplate?.footer || '';
    const replay = useArcadeReplay();
    const wideShell = growPlayfield
        ? { ...GROW_PLAYFIELD_SHELL_LAYOUT, ...(shellLayoutOverrides || {}) }
        : GROW_PLAYFIELD_SHELL_LAYOUT;
    // Replay is keyed off a DB run id, not formatScore / gameId. Missing score
    // formatters used to hide the control even when GET /api/runs/:id would work.

    const scoreLabel = (entry) => {
        if (typeof formatScore === 'function') return formatScore(entry);
        const s = leaderboardEntryScore(entry);
        return typeof s === 'number' && !Number.isInteger(s) ? s.toFixed(1) : String(s);
    };

    const leaderboardPanel = (
        <>
            <LeaderboardPanel
                entries={leaderboard}
                loading={leaderboardLoading}
                onRefresh={refreshLeaderboard}
                accentColor={accent}
                formatScore={scoreLabel}
                emptyHint={emptyLeaderboardHint}
                renderAction={(entry) => (
                    <LeaderboardWatchButton
                        entry={entry}
                        onWatch={replay.openReplay}
                        accentColor={accent}
                    />
                )}
            />
            {(belowLeaderboard || sidebarExtra) ? (
                <Box sx={{ mt: 2 }}>{belowLeaderboard || sidebarExtra}</Box>
            ) : null}
        </>
    );

    const playColumn = (
        <Paper
            sx={{
                p: growPlayfield ? wideShell.playColumnPadding : 3,
                bgcolor: playfieldDark ? '#121214' : THEME.cardBg,
                border: `1px solid ${THEME.border}`,
                borderRadius: 3,
                minHeight: growPlayfield ? { xs: 480, lg: 620 } : 320,
                ...(growPlayfield
                    ? {
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        minWidth: 0,
                        ...(playfieldDark
                            ? {
                                boxShadow: '0 0 20px rgba(0,0,0,0.45), inset 0 1px 0 rgba(255,255,255,0.04)',
                            }
                            : null),
                    }
                    : null),
            }}
        >
            {agentRunLoading ? (
                <Box
                    data-testid="agent-running"
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: growPlayfield ? 280 : 180,
                        color: THEME.secondary,
                    }}
                >
                    <Typography sx={{ color: 'white', fontWeight: 700 }}>
                        Running Agent
                    </Typography>
                </Box>
            ) : growPlayfield ? (
                <Box sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                }}>
                    {children}
                </Box>
            ) : children}
            <GmLeaseNotice lease={gmLease} color={accent} />
            <ManualSaveNotice notice={manualSaveNotice} color={accent} />
            {showAgentReplay && !agentReplayDismissed ? (
                <Box sx={{ mt: 2 }} data-testid="agent-replay">
                    <ReplayPlayer run={run} onClose={() => setAgentReplayDismissed(true)} />
                </Box>
            ) : null}
        </Paper>
    );

    const agentPanel = (
        <>
            <Paper
                sx={{
                    p: growPlayfield ? wideShell.sidePanelPadding : 3,
                    bgcolor: THEME.cardBg,
                    border: `1px solid ${THEME.border}`,
                    borderRadius: 3,
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Code sx={{ color: accent }} />
                    <Typography variant="h6" sx={{ color: 'white', fontWeight: 'bold' }}>
                        Agent Mode
                    </Typography>
                </Box>

                {!token ? (
                    <Box
                        sx={{
                            border: `1px dashed ${COLOR.borderStrong}`,
                            borderRadius: 2,
                            p: 3,
                            textAlign: 'center',
                            mb: 2,
                            bgcolor: 'rgba(255,255,255,0.02)',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <Lock sx={{ color: THEME.secondary, fontSize: 30 }} />
                        <Typography variant="body2" sx={{ color: THEME.secondary }}>
                            Sign in to upload or edit an agent.
                        </Typography>
                        <Link to="/auth/login" style={{ textDecoration: 'none' }}>
                            <Button
                                size="small"
                                variant="outlined"
                                sx={{ mt: 1, borderColor: accent, color: accent }}
                            >
                                Sign in
                            </Button>
                        </Link>
                    </Box>
                ) : (
                    <>
                        <Box
                            sx={{
                                display: 'flex',
                                mb: 2,
                                borderBottom: `1px solid ${THEME.border}`,
                            }}
                        >
                            {['upload', 'editor'].map((tab) => (
                                <Button
                                    key={tab}
                                    size="small"
                                    onClick={() => setAgentInputTab(tab)}
                                    sx={{
                                        flex: 1,
                                        textTransform: 'capitalize',
                                        borderRadius: 0,
                                        borderBottom:
                                            agentInputTab === tab
                                                ? `2px solid ${accent}`
                                                : '2px solid transparent',
                                        color:
                                            agentInputTab === tab
                                                ? accent
                                                : THEME.secondary,
                                        fontWeight: agentInputTab === tab ? 700 : 400,
                                        pb: 1,
                                    }}
                                >
                                    {tab === 'upload' ? 'Upload File' : 'Code Editor'}
                                </Button>
                            ))}
                        </Box>

                        {agentInputTab === 'upload' ? (
                            <>
                                <Box
                                    sx={{
                                        border: `1px dashed ${COLOR.borderStrong}`,
                                        borderRadius: 2,
                                        p: 2,
                                        textAlign: 'center',
                                        mb: 2,
                                        bgcolor: 'rgba(255,255,255,0.02)',
                                    }}
                                >
                                    <input
                                        id={`agent-upload-${title}`}
                                        type="file"
                                        accept=".py"
                                        style={{ display: 'none' }}
                                        onChange={handleAgentFileChange}
                                    />
                                    <label htmlFor={`agent-upload-${title}`}>
                                        <Button
                                            component="span"
                                            startIcon={<CloudUpload />}
                                            sx={{ color: THEME.secondary, textTransform: 'none' }}
                                        >
                                            {agentFile ? agentFile.name : 'Select Python Agent'}
                                        </Button>
                                    </label>
                                </Box>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleRunAgent}
                                    disabled={!agentFile || agentRunLoading}
                                    sx={{
                                        bgcolor: '#2563eb',
                                        color: 'white',
                                        mb: 2,
                                        '&:disabled': {
                                            bgcolor: '#1e293b',
                                            color: '#64748b',
                                        },
                                    }}
                                >
                                    {agentRunLoading
                                        ? 'Running Agent'
                                        : 'Upload Agent'}
                                </Button>
                            </>
                        ) : (
                            <AgentCodeEditor
                                header={header}
                                template={template}
                                footer={footer}
                                loading={agentRunLoading}
                                theme={{ ...THEME, primary: accent }}
                                onSubmit={handleEditorSubmit}
                            />
                        )}
                    </>
                )}

                {uploadMessage && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            mt: 1,
                            color: uploadNeedsAccount || /fail|error|sign in|expired/i.test(uploadMessage)
                                ? THEME.accent
                                : accent,
                            textAlign: 'center',
                        }}
                    >
                        {uploadMessage}
                    </Typography>
                )}
                {uploadMessage && uploadNeedsAccount && <UploadAccountLinks color={accent} />}

                {/*
                    Persistent "Last run" card: always sourced from the backend
                    (`GET /api/runs/last`), never from the transient upload-response
                    state. This renders on mount (before any upload happens this
                    session), survives re-renders/remounts/polling, and is refreshed
                    — not replaced — after a new upload completes. That deterministic
                    server-fetch (vs. ephemeral client state) is what eliminates the
                    cross-browser flicker.
                */}
                <Box sx={{ mt: 2 }}>
                    <Typography
                        variant="caption"
                        sx={{
                            color: THEME.secondary,
                            display: 'block',
                            mb: 0.5,
                            fontWeight: 'bold',
                            textTransform: 'uppercase',
                            fontSize: '0.65rem',
                            letterSpacing: 0.5,
                        }}
                    >
                        Last run
                    </Typography>
                    {lastRun ? (
                        <Paper
                            data-testid="last-run-card"
                            sx={{
                                p: 1.5,
                                bgcolor: lastRunCardBg,
                                border: `1px solid ${accent}`,
                                borderRadius: 2,
                            }}
                        >
                            <Typography
                                variant="body2"
                                sx={{ color: 'white', fontWeight: 600, mb: 0.5 }}
                            >
                                {String(lastRun.name).startsWith('agent_')
                                    ? lastRun.name
                                    : `agent_${lastRun.name}`}
                            </Typography>
                            <Stack direction="row" spacing={1} alignItems="center">
                                <Chip
                                    label={`Score: ${lastRun.score}`}
                                    size="small"
                                    sx={{
                                        bgcolor: accent,
                                        color: lastRunChipText,
                                        fontWeight: 'bold',
                                        height: 20,
                                        fontSize: '0.7rem',
                                    }}
                                />
                                <Typography variant="caption" sx={{ color: COLOR.textMuted }}>
                                    {formatArcadeTimestamp(lastRun.time)}
                                </Typography>
                                {showLastRunReplay ? (
                                    <>
                                        <LeaderboardWatchButton
                                            entry={lastRun.raw || lastRun}
                                            onWatch={(entry) => replay.openReplay(entry, { loop: false })}
                                            accentColor={accent}
                                        />
                                        <Button
                                            size="small"
                                            data-testid="last-run-loop"
                                            aria-label="Loop replay"
                                            onClick={() => replay.openReplay(lastRun.raw || lastRun, { loop: true })}
                                            sx={{
                                                minWidth: 28,
                                                width: 28,
                                                height: 28,
                                                p: 0,
                                                color: accent,
                                                borderRadius: 1.5,
                                            }}
                                        >
                                            <Loop fontSize="small" />
                                        </Button>
                                    </>
                                ) : null}
                            </Stack>
                        </Paper>
                    ) : lastRunLoading ? (
                        <Paper
                            data-testid="last-run-card-loading"
                            sx={{
                                p: 1.5,
                                bgcolor: 'rgba(255,255,255,0.04)',
                                border: `1px solid ${THEME.border}`,
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: THEME.secondary }}>
                                Loading…
                            </Typography>
                        </Paper>
                    ) : (
                        <Paper
                            data-testid="last-run-card-empty"
                            sx={{
                                p: 1.5,
                                bgcolor: 'rgba(255,255,255,0.04)',
                                border: `1px solid ${THEME.border}`,
                                borderRadius: 2,
                            }}
                        >
                            <Typography variant="caption" sx={{ color: THEME.secondary }}>
                                No runs yet
                            </Typography>
                        </Paper>
                    )}
                </Box>
            </Paper>
            {belowAgent ? (
                <Box sx={{ mt: 2 }}>{belowAgent}</Box>
            ) : null}
        </>
    );

    return (
        <ArcadeShellLayout
            title={title}
            titleAccent={titleAccent}
            subtitle={subtitle}
            accentColor={accent}
            headerContent={headerContent}
            growPlayfield={growPlayfield}
            wideShell={wideShell}
            leaderboard={leaderboardPanel}
            play={playColumn}
            agent={agentPanel}
        >
            <ArcadeReplayDialog
                open={replay.open}
                loading={replay.loading}
                error={replay.error}
                run={replay.run}
                startLoop={replay.startLoop}
                onClose={replay.closeReplay}
            />
        </ArcadeShellLayout>
    );
}

export { THEME as SHELL_THEME };
